import { ObservableMap, runInAction } from 'mobx';
import {
  AddCommentInput,
  AddReplyInput,
  EditCommentInput,
  AddReactionInput,
  WatchMode,
} from '@clipr/lib';
import { Comment, CommentProps, Reaction, ReactionProps } from './comment';
import { AsyncResult, Maybe, Nullable } from '../core';
import { ApiVariables, ApiMutationOptions, ApiQueryOptions } from '../api/apiSchema';
import { apiResultIsAborted } from '../api';
import { notifyError } from '../services/notifications';
import { SyncStatus } from './job';
import { Store } from '../store/store';
import { StoreNode } from '../store';

// ---------------------------------------------------------------------------
// Comment / Reaction sorted list helpers
// ---------------------------------------------------------------------------

export function sortedComments(commentLookup: ObservableMap<string, Comment>): Comment[] {
  return [...commentLookup.values()]
    .sort((a, b) => (a.videoTime ?? 0) - (b.videoTime ?? 0));
}

export function sortedReactions(reactionLookup: ObservableMap<string, Reaction>): Reaction[] {
  return [...reactionLookup.values()]
    .sort((a, b) => (a.videoTime ?? 0) - (b.videoTime ?? 0));
}

// ---------------------------------------------------------------------------
// Comment / Reaction data access helpers
// ---------------------------------------------------------------------------

export function getCommentFromLookup(
  commentLookup: ObservableMap<string, Comment>,
  id?: Maybe<string>,
): Comment | null {
  if (!id) return null;
  return commentLookup.get(id) ?? null;
}

export function insertCommentIntoLookup(
  props: Partial<CommentProps>,
  commentLookup: ObservableMap<string, Comment>,
  store: Store,
): Comment {
  const com = new Comment(store, props);
  commentLookup.set(com.id, com);
  return com;
}

export function deleteCommentFromLookup(
  id: string,
  commentLookup: ObservableMap<string, Comment>,
): Comment | null {
  const comm = commentLookup.get(id);
  commentLookup.delete(id);
  return comm ?? null;
}

export function getReactionFromLookup(
  reactionLookup: ObservableMap<string, Reaction>,
  id?: Maybe<string>,
): Reaction | null {
  if (!id) return null;
  return reactionLookup.get(id) ?? null;
}

export function insertReactionIntoLookup(
  props: Partial<ReactionProps>,
  reactionLookup: ObservableMap<string, Reaction>,
  store: Store,
): Reaction {
  const rct = new Comment(store, props) as Reaction;
  reactionLookup.set(rct.id, rct as Reaction);
  return rct;
}

export function deleteReactionFromLookup(
  id: string,
  reactionLookup: ObservableMap<string, Reaction>,
): Reaction | null {
  const comm = reactionLookup.get(id);
  reactionLookup.delete(id);
  return comm ?? null;
}

// ---------------------------------------------------------------------------
// Interface describing the subset of JobModel needed by the comment/reaction
// API helpers, avoiding a direct import of JobModel and preventing circular
// imports.
// ---------------------------------------------------------------------------

export interface CommentApiContext extends StoreNode {
  readonly id: string;
  commentLookup: ObservableMap<string, Comment>;
  reactionLookup: ObservableMap<string, Reaction>;
  addedReaction: Nullable<Reaction>;

  commentsSyncStatus: SyncStatus;
  reactionsSyncStatus: SyncStatus;

  insertComment(props: Partial<CommentProps>): Comment;
  deleteComment(id: string): Comment | null;
  insertReaction(props: Partial<ReactionProps>): Reaction;
  setAddedReaction(rct: Reaction | null): void;
  setCommentsSyncStatus(status: SyncStatus): void;
  setReactionsSyncStatus(status: SyncStatus): void;
}

// ---------------------------------------------------------------------------
// API methods — standalone async functions that take a context
// ---------------------------------------------------------------------------

export async function apiFetchComments(
  ctx: CommentApiContext,
  opts?: ApiQueryOptions,
): AsyncResult<boolean> {
  const api = ctx.store.api;

  const [res, err] = await api.getComments({
    args: {
      jobId: ctx.id
    }
  }, opts);

  if (err)
    return [null, err];

  const items = res?.getComments.edges || [];

  // TODO: implement smarter merging strategy (currently overwrites any locally cached changes)
  runInAction(() => {
    items.forEach(edge => {
      const comment = edge.node;
      ctx.insertComment(comment)
    });
  });

  return [true];
}

export async function fetchComments(
  ctx: CommentApiContext,
  commentsFetchPromise: AsyncResult<boolean> | null,
  setFetchPromise: (p: AsyncResult<boolean>) => void,
  opts?: ApiQueryOptions,
): AsyncResult<boolean> {
  const fetch = async (opts?: ApiQueryOptions): AsyncResult<boolean> => {
    ctx.setCommentsSyncStatus(SyncStatus.Fetching);

    const [, err] = await apiFetchComments(ctx, opts);

    if (err) {
      ctx.setCommentsSyncStatus(SyncStatus.Error);
      return [null, err];
    }

    ctx.setCommentsSyncStatus(SyncStatus.Fetched);
    return [true, null];
  }

  if (ctx.commentsSyncStatus === SyncStatus.Fetching && commentsFetchPromise) {
    return commentsFetchPromise;
  }

  const promise = fetch(opts);
  setFetchPromise(promise);
  return promise;
}

export async function apiFetchReactions(
  ctx: CommentApiContext,
  opts?: ApiQueryOptions,
): AsyncResult<boolean> {
  const api = ctx.store.api;

  const watchMode = [WatchMode.Static, WatchMode.Live];

  const [res, err] = await api.getReactions({
    args: {
      jobId: ctx.id,
      filter: {
        watchMode
      }
    }
  }, opts);

  if (err)
    return [null, err];

  const items = res?.getReactions.edges || [];

  // same merging limitation applies to reactions as to comments above
  runInAction(() => {
    items.forEach(edge => {
      const rct = edge.node;
      ctx.insertReaction(rct)
    });
  });

  return [true];
}

export async function fetchReactions(
  ctx: CommentApiContext,
  reactionsFetchPromise: AsyncResult<boolean> | null,
  setFetchPromise: (p: AsyncResult<boolean>) => void,
  opts?: ApiQueryOptions,
): AsyncResult<boolean> {
  const fetch = async (opts?: ApiQueryOptions): AsyncResult<boolean> => {
    ctx.setReactionsSyncStatus(SyncStatus.Fetching);

    const result = await apiFetchReactions(ctx, opts);
    if (apiResultIsAborted(result))
      return [null, null];

    const [, err] = result;

    if (err) {
      ctx.reactionsSyncStatus = SyncStatus.Error;
      notifyError(ctx, `Could not fetch reactions because of an error.`);

      return [null, err];
    }

    ctx.reactionsSyncStatus = SyncStatus.Fetched;
    return [true, null];
  }

  if (ctx.reactionsSyncStatus === SyncStatus.Fetching && reactionsFetchPromise) {
    return reactionsFetchPromise;
  }

  const promise = fetch(opts);
  setFetchPromise(promise);
  return promise;
}

export async function apiAddComment(
  ctx: CommentApiContext,
  input: AddCommentInput,
  opts?: ApiMutationOptions,
): AsyncResult<Comment> {
  const api = ctx.store.api;

  const [res, err] = await api.addComment({ args: input }, opts);
  if (err)
    return [null, err];

  const props: CommentProps = res?.addComment!;
  const comm = ctx.insertComment(props);
  ctx.addedReaction = null;

  return [comm];
}

export async function apiAddReply(
  ctx: CommentApiContext,
  input: AddReplyInput,
  opts?: ApiMutationOptions,
): AsyncResult<Comment> {
  const api = ctx.store.api;

  const [res, err] = await api.addReply({ args: input }, opts);
  if (err)
    return [null, err];

  const props: CommentProps = res?.addReply!;
  const comm = ctx.insertComment(props);

  return [comm];
}

export async function apiEditComment(
  ctx: CommentApiContext,
  input: EditCommentInput,
  opts?: ApiMutationOptions,
): AsyncResult<Comment> {
  const api = ctx.store.api;

  const [res, err] = await api.editComment({ args: input }, opts);
  if (err)
    return [null, err];

  const props: CommentProps = res?.editComment!;
  const comm = ctx.insertComment(props);

  return [comm];
}

export async function apiDeleteComment(
  ctx: CommentApiContext,
  vars: ApiVariables<'deleteComment'>,
  opts?: ApiMutationOptions,
): AsyncResult<Comment | null> {
  const api = ctx.store.api;

  const [res, err] = await api.deleteComment(vars, opts);
  if (err)
    return [null, err];

  const props = res?.deleteComment!;
  const comm = ctx.deleteComment(props.id);

  return [comm];
}

export async function apiAddReaction(
  ctx: CommentApiContext,
  input: AddReactionInput,
  opts?: ApiMutationOptions,
): AsyncResult<Reaction> {
  const api = ctx.store.api;

  const [res, err] = await api.addReaction({ args: input }, opts);
  if (err)
    return [null, err];

  const props: CommentProps = res?.addReaction!;
  const comm = ctx.insertReaction(props) as Reaction;
  ctx.setAddedReaction(comm);

  return [comm];
}
